import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, Loader2, AlertTriangle, Eye, Ban, ShieldCheck, Camera, MonitorPlay } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_BADGE = {
  active: "bg-indigo-400/10 text-indigo-400",
  completed: "bg-emerald-400/10 text-emerald-400",
  terminated: "bg-rose-400/10 text-rose-400",
};

export default function ProctoringSessionsPanel() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.session_type = typeFilter;
      const { data } = await api.get("/admin/proctoring-sessions", { params });
      setSessions(data);
    } catch (e) {
      toast.error("Could not load proctoring sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSessions(); }, [statusFilter, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/admin/proctoring-sessions/${id}`);
      setSelected(data);
    } catch (e) {
      toast.error("Could not load session detail");
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div data-testid="admin-proctoring-panel">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-proctoring-status-filter"
          className="text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-200">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="terminated">Terminated</option>
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="admin-proctoring-type-filter"
          className="text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-200">
          <option value="">All Flows</option>
          <option value="classic">Classic (Text)</option>
          <option value="live">Live Voice</option>
        </select>
      </div>

      {loading && (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>
      )}

      {!loading && sessions.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-8" data-testid="admin-proctoring-empty">No proctoring sessions found for this filter.</p>
      )}

      <div className="space-y-2.5">
        {sessions.map((s) => (
          <div key={s.id} className="glass-panel rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-proctoring-row-${s.id}`}>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-white">{s.user_name}</p>
                <span className="text-xs text-slate-500">{s.user_email}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_BADGE[s.status] || "bg-slate-500/10 text-slate-400"}`}>{s.status}</span>
              </div>
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
                <span className="capitalize">{s.session_type} flow</span>
                <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {s.violation_count} violation{s.violation_count === 1 ? "" : "s"}</span>
                <span>{new Date(s.started_at).toLocaleString()}</span>
                {(s.webcam_recording_path || s.screen_recording_path) && (
                  <span className="flex items-center gap-1 text-indigo-400"><Video className="w-3 h-3" /> Recording available</span>
                )}
              </p>
            </div>
            <button onClick={() => openDetail(s.id)} data-testid={`admin-proctoring-view-btn-${s.id}`}
              className="flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/15 text-white px-3 py-1.5 rounded-lg transition-colors">
              <Eye className="w-3.5 h-3.5" /> View
            </button>
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-white/10 text-white max-h-[85vh] overflow-y-auto" data-testid="admin-proctoring-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              Proctoring Session — {selected?.user_name}
            </DialogTitle>
          </DialogHeader>
          {loadingDetail && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>}
          {selected && !loadingDetail && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap text-xs text-slate-400">
                <span>{selected.user_email}</span>
                <span className="capitalize">· {selected.interview_type || "unknown"} interview</span>
                <span className={`font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_BADGE[selected.status] || "bg-slate-500/10 text-slate-400"}`}>{selected.status}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div data-testid="admin-proctoring-webcam-player">
                  <p className="text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Webcam Recording</p>
                  {selected.webcam_recording_url ? (
                    <video controls className="w-full rounded-lg border border-white/10" src={`${process.env.REACT_APP_BACKEND_URL}${selected.webcam_recording_url}`} />
                  ) : (
                    <p className="text-xs text-slate-500 bg-white/5 rounded-lg p-4 text-center">No webcam recording</p>
                  )}
                </div>
                <div data-testid="admin-proctoring-screen-player">
                  <p className="text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5"><MonitorPlay className="w-3.5 h-3.5" /> Screen Recording</p>
                  {selected.screen_recording_url ? (
                    <video controls className="w-full rounded-lg border border-white/10" src={`${process.env.REACT_APP_BACKEND_URL}${selected.screen_recording_url}`} />
                  ) : (
                    <p className="text-xs text-slate-500 bg-white/5 rounded-lg p-4 text-center">No screen recording</p>
                  )}
                </div>
              </div>

              <div data-testid="admin-proctoring-violations-list">
                <p className="text-xs font-semibold text-slate-300 mb-2 flex items-center gap-1.5">
                  {selected.violation_count > 0 ? <Ban className="w-3.5 h-3.5 text-rose-400" /> : <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />}
                  Violations ({selected.violation_count})
                </p>
                {selected.violations?.length === 0 && <p className="text-xs text-slate-500">No violations recorded — clean session.</p>}
                <div className="space-y-1.5">
                  {(selected.violations || []).map((v, i) => (
                    <div key={i} className="text-xs bg-white/5 rounded-lg px-3 py-2 flex items-center justify-between">
                      <span className="text-rose-300 font-medium">{v.type.replace(/_/g, " ")}</span>
                      <span className="text-slate-500">{new Date(v.timestamp).toLocaleTimeString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
